import { useJsApiLoader } from "@react-google-maps/api";
import { useState, useEffect } from "react";
import { StyledTextField } from "../../../../styled-mui";
import { StyledBox } from "@hcl-commerce-store-sdk/react-component";
import { GMAPS_API_LOADER_CONFIG } from "../../../../configs/common";
import GoogleSearchAutocompleteCity from "../../google-search-autocomplete/google-search-autocomplete-city";

interface Props {
  value?: string; // current city
  onCityChange?: (city: string) => void;
  onPostalCodeChange?: (postalCode: string) => void;
  error?: boolean;
}

export const CityAutoSuggest = ({ value = "", onCityChange, onPostalCodeChange, error }: Props) => {
  const { isLoaded } = useJsApiLoader(GMAPS_API_LOADER_CONFIG);
  const [city, setCity] = useState(value);
  const [searchResult, setSearchResult] = useState<google.maps.places.Autocomplete>();

  // Sync internal state with parent value
  useEffect(() => {
    setCity(value);
  }, [value]);

  const handlePlaceSelect = async (
    autocomplete: google.maps.places.Autocomplete,
    placeResult?: google.maps.places.PlaceResult
  ) => {
    const place = placeResult ?? autocomplete.getPlace();
    if (!place?.geometry?.location) return;

    // 1️⃣ Extract city name
    const cityName =
      place.address_components?.find((c) => c.types.includes("locality"))?.long_name ||
      place.address_components?.find((c) => c.types.includes("administrative_area_level_2"))?.long_name ||
      place.name ||
      "";

    if (!cityName) return;

    setCity(cityName);
    onCityChange?.(cityName);

    // 2️⃣ Try postal code from selected place itself
    const directPostalCode = place.address_components?.find((c) => c.types.includes("postal_code"))?.long_name;

    if (directPostalCode) {
      onPostalCodeChange?.(directPostalCode);
      return;
    }

    // 3️⃣ Reverse geocode using LAT/LNG → nearest postal code (WORKS FOR PARIS)
    const geocoder = new google.maps.Geocoder();

    geocoder.geocode(
      {
        location: {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
        },
      },
      (results, status) => {
        if (status === "OK" && results?.length) {
          for (const result of results) {
            const postal = result.address_components.find((c) => c.types.includes("postal_code"))?.long_name;

            if (postal) {
              onPostalCodeChange?.(postal);
              return;
            }
          }
        }

        // 4️⃣ Final fallback ONLY if Google truly gives nothing
        onPostalCodeChange?.("");
      }
    );
  };

  return (
    <>
      {isLoaded && (
        // <GoogleSearchAutocompleteCity
        //   searchResult={searchResult}
        //   setSearchResult={setSearchResult}
        //   onPlaceSelect={handlePlaceSelect}>

        // </GoogleSearchAutocompleteCity>
        <StyledTextField
          variant="standard"
          focused
          className="styled-v2"
          fullWidth
          placeholder=""
          required
          label={
            <>
              Ville
              <StyledBox component="span" color="primary.main" sx={{ pl: 1 }}>
                *
              </StyledBox>
            </>
          }
          value={city}
          onChange={(e) => {
            setCity(e.target.value);
            onCityChange?.(e.target.value); // update parent on typing
          }}
          error={error}
          helperText={error ? "Ville obligatoire" : ""}
        />
      )}
    </>
  );
};
